import React from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { Text, FormatDate, color } from '@morressier/ts';

import { useMediaQuery } from '@material-ui/core';

import { FeatureFlags } from 'config/featureFlags';
import { BREAKPOINT_MEDIUM } from 'constants/breakpoints';
import { RegOrBuyTicket } from 'containers/SingleSession/Modal/RegOrBuyTicketRequired';
import Video from 'containers/SingleSession/Video';
import { useAuthedUser } from 'hooks/useIsAuthedUser';
import { useDocumentVideo } from 'services/documents';
import { useAuthContext, useEventContext } from 'store';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { checkIsAsce, checkPaywal } from 'utils/checkPaywall';
import { logEvent } from 'utils/eventTracking';

import OrganizationLink from './OrganizationLink';
import * as Styles from './styles';

type VideoSectionProps = {
  event_id: string;
  organization_id?: string;
  article_id: string;
  article_title: string;
  event: MorressierEvent;
  videoSession: MorressierVideoSession | null;
  articleVideoPresentation: MorressierVideoPresentation | undefined;
  organization_data?: MorressierOrganization;
  submissionDate?: string;
  isDPCEvent: boolean;
  dpcCustomEventName?: string;
};

const VideoSection: React.FC<VideoSectionProps> = props => {
  const {
    organization_id,
    event_id,
    article_id,
    event,
    article_title,
    articleVideoPresentation,
    videoSession,
    organization_data,
    submissionDate,
    isDPCEvent,
    dpcCustomEventName,
  } = props;

  const { query } = useRouter();
  const isDesktop = useMediaQuery(`(min-width:${BREAKPOINT_MEDIUM}px)`);
  const contentLibraryLink =
    query.contentLibrary && query.contentLibraryTitle
      ? `?contentLibrary=${query.contentLibrary}&contentLibraryTitle=${query.contentLibraryTitle}`
      : '';
  const eventHome = `/event/${event.id}${contentLibraryLink}`;
  const isAuthed = useAuthedUser();

  const { state: authState } = useAuthContext();
  const { state: ticketingState } = useRegAndTicketingContext();
  const canShowVideo = event.embargoed ? isAuthed : true;
  const { data, isLoading } = useDocumentVideo({
    organization_id,
    event_id,
    article_id,
    canShowVideo,
  });

  const { isTicketedEvent } = ticketingState;
  const hasAccess = ticketingState.userHasAccess;
  const isTicketingFlagEnabled = FeatureFlags.Event_Ticketing.isEnabled();
  const hasEventEnded = new Date() > new Date(event.end_date);

  const canShowVideoForTicketedEVent = () => {
    if (!isTicketingFlagEnabled) return true;

    if (isTicketedEvent) {
      return hasEventEnded ? isAuthed : hasAccess;
    }

    return isAuthed;
  };

  const validVideoSession = videoSession && Object.keys(videoSession).length !== 0;
  const hasVideo = Boolean(articleVideoPresentation || validVideoSession);

  const logRecorderVideoView = () => {
    logEvent('VIEWED_VIDEO_PRESENTATION', {
      id: article_id,
      event_id,
    });
  };

  const logWatchTime = (args: { watchedFor: number }) => {
    logEvent('WATCHED_VIDEO_PRESENTATION', {
      id: article_id,
      event_id,
      watched_for: args.watchedFor,
    });
  };
  const actionOnEvent = isDPCEvent ? 'Submitted on' : 'Presented at';

  // TODO: make a query to ACAPI to check if theres a paywall
  const isAsce = checkIsAsce(organization_id);
  const hasPaywall = checkPaywal(organization_id);
  const showSubscribe = !isLoading && !data?.signed_url && hasPaywall;

  return (
    <Styles.VideoSectionGrid>
      {hasVideo && (
        <Styles.VideoPlayerColumn>
          <div
            style={{
              position: 'relative',
              width: '100%',
              background: showSubscribe || !hasVideo ? 'black' : 'none',
            }}
          >
            {showSubscribe && (
              <div
                style={{
                  minHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <h3
                  style={{
                    color: '#FFF',
                    textAlign: 'center',
                    margin: '16px 48px',
                    fontSize: '1.25rem',
                    lineHeight: '1.75rem',
                  }}
                >
                  This video can not be played without {`${organization_data?.acronym}'s`}{' '}
                  subscription. Check subscription options by clicking the link below.
                </h3>
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={
                    isAsce
                      ? 'https://ascelibrary.org/page/subscribetoascelibrarypackages'
                      : 'https://www.asme.org/publications-submissions/publishing-information/how-to-buy'
                  }
                >
                  <a
                    style={{
                      fontWeight: 600,
                      fontSize: '1.5rem',
                      lineHeight: '1.75rem',
                      color: '#FFF',
                    }}
                  >
                    How to buy
                  </a>
                </a>
              </div>
            )}

            {data?.signed_url && (
              <>
                {isTicketingFlagEnabled && isTicketedEvent ? (
                  <>
                    {canShowVideoForTicketedEVent() ? (
                      <Video
                        authUserId={authState?.authInfo?.user_id}
                        poster={{
                          title: article_title,
                          id: article_id,
                          video_presentation: articleVideoPresentation,
                        }}
                        videoSession={videoSession}
                        onPlay={logRecorderVideoView}
                        logWatchTime={logWatchTime}
                        containerStyles={{
                          paddingTop: 'unset',
                          width: '100%',
                          height: '100%',
                          minHeight: isDesktop ? '456px' : '300px',
                        }}
                        pictureInPictureToggle={false}
                      />
                    ) : (
                      <RegOrBuyTicket
                        style={{
                          width: '100%',
                          height: '100%',
                          minHeight: isDesktop ? '456px' : '300px',
                        }}
                      />
                    )}
                  </>
                ) : (
                  <Video
                    authUserId={authState?.authInfo?.user_id}
                    poster={{
                      title: article_title,
                      id: article_id,
                      video_presentation: articleVideoPresentation,
                    }}
                    organizationId={organization_data?._id || ''}
                    videoSession={videoSession}
                    onPlay={logRecorderVideoView}
                    logWatchTime={logWatchTime}
                    containerStyles={{
                      paddingTop: 'unset',
                      width: '100%',
                      height: '100%',
                      minHeight: isDesktop ? '456px' : '300px',
                    }}
                    pictureInPictureToggle={false}
                  />
                )}
              </>
            )}
          </div>
        </Styles.VideoPlayerColumn>
      )}
      <Styles.VideoInfoColumn hasVideo={hasVideo}>
        <Styles.ProductLabel className="mb1">{actionOnEvent}</Styles.ProductLabel>
        {event ? (
          <>
            {isDPCEvent ? (
              <Text
                as="a"
                onClick={() => {
                  logEvent('EVENT_CLICKED', {
                    eventPosition: 'ARTICLE_PAGE',
                    id: event.id,
                  });
                }}
                fontWeight="semiBold"
                style={{
                  fontSize: '1.5rem',
                  lineHeight: '2rem',
                  cursor: 'auto',
                  marginBottom: '0.5rem',
                }}
              >
                {dpcCustomEventName}
              </Text>
            ) : (
              <Link href={eventHome} passHref>
                <Text
                  as="a"
                  onClick={() => {
                    logEvent('EVENT_CLICKED', {
                      eventPosition: 'ARTICLE_PAGE',
                      id: event.id,
                    });
                  }}
                  fontWeight="semiBold"
                  style={{
                    fontSize: '1.5rem',
                    lineHeight: '2rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    marginBottom: '0.5rem',
                  }}
                >
                  {event.name}
                </Text>
              </Link>
            )}
            <Text color="grey">
              <FormatDate
                startDate={isDPCEvent && submissionDate ? submissionDate : event.start_date}
                endDate={isDPCEvent && submissionDate ? submissionDate : event.end_date}
              />
            </Text>
          </>
        ) : (
          <Text color="grey">No conference information is available for this submission.</Text>
        )}
        {organization_data?.content_library && (
          <>
            <OrganizationLink
              organizationData={organization_data}
              contentLibrary={(query.contentLibraryTitle as string) || organization_data.name}
            />
          </>
        )}
      </Styles.VideoInfoColumn>
    </Styles.VideoSectionGrid>
  );
};

export default VideoSection;
